import logoUrl from '@/assets/logo.png';
import whiteLogoUrl from '@/assets/logo_white.png';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import { useCurrentUser } from '../../providers/currentUser/CurrentUserProvider';
import { isStytchError } from './common';
import { useColorScheme } from "@mui/material/styles";

type LoginForm = {
    email: string;
    password: string;
};

const Login = (): ReactElement => {
    const [orgSlug, setOrgSlug] = useState(getOrganizationSlug() || "");
    const stytch = useStytchB2BClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const { mode } = useColorScheme();
    const { refusedStatus, verificationFailed, clearRefusal } = useCurrentUser();

    // Stytch accepted the credentials but our API turned the account away, so the session was revoked and we
    // are back here. Say why: without this the sign-in simply appears to do nothing, which is the worst of
    // both worlds — refused, but indistinguishable from a bug.
    //
    // The wording is ours rather than the server's. A disabled account surfaces as UnauthorizedAccessException,
    // which PortalAPI maps to 403 with messageIsForTheUser: false, so its detail is redacted outside
    // Development. 403 is specifically "we know you and you are switched off"; 401/404 mean the account is no
    // longer there at all.
    // A failure to CHECK is not a refusal. Without this branch an API outage would drop a perfectly valid user
    // back here with no explanation at all — the gate holds them out either way, so it has to say which.
    const refusalMessage = refusedStatus !== undefined
        ? (refusedStatus === 403 ? strings.accountDisabledMessage : strings.accountNoAccessMessage)
        : verificationFailed
            ? strings.accountVerificationFailedMessage
            : undefined;
    const { register, handleSubmit: validateAndSubmit, getValues, setError, clearErrors, formState } = useForm<LoginForm>({
        defaultValues: { email: '', password: '' },
    });

    function getOrganizationSlug(): string | null {
        const path = window.location.pathname;
        const index = path.indexOf('/', 1);
        if (index > 0) {
            return path.substring(1, index);
        }
        else {
            return path.substr(1);
        }
    }

    async function getOrgIdFromSlug() {
        // Authenticate requires organization_id, so first get the organization by slug (which is in the subdomain)            
        if (!orgSlug) {
            toast({ severity: "error", message: strings.missingTenant });
            throw strings.missingTenant;
        }

        const response = await stytch.organization.getBySlug({ organization_slug: orgSlug });
        if (!response || !response.organization) {
            toast({ severity: "error", message: strings.orgNotFound.replace("{{slug}}", orgSlug) });
            throw strings.orgNotFound.replace("{{slug}}", orgSlug);
        }

        return response.organization.organization_id as string
    }

    const handleLogin = async ({ email, password }: LoginForm) => {
        clearErrors('root');
        // Drop any previous refusal before trying again, so the old reason cannot linger over a new attempt —
        // and so the account check genuinely re-runs rather than being served the cached failure.
        clearRefusal();
        try {
            const orgId = await getOrgIdFromSlug();
            const authenticateResponse = await stytch.passwords.authenticate({
                organization_id: orgId,
                email_address: email,
                password,
                session_duration_minutes: parseInt(import.meta.env.VITE_STYTCH_SESSION_DURATION),
            });

            // Seed the org slug (login identity) from the authenticated org itself, not the typed input, so it
            // stays perfectly in lockstep with isSuperAdmin (which also reads the Stytch org). The typed slug
            // is only a fallback. This is identity, not the viewed org — a super-admin's picker never changes it.
            app.setOrganizationSlug(authenticateResponse.organization?.organization_slug ?? orgSlug);
        }
        catch (err) {
            console.error('Error:', err);
            if (isStytchError(err)) {
                setError('root', {
                    type: 'server',
                    message: err.error_type === "unauthorized_credentials" ? strings.loginError : err.error_message,
                });
            } else {
                setError('root', { type: 'server', message: 'An unexpected error occurred' });
            }
        }
    };

    // Stytch validates every redirect URL against an allow-list registered in its dashboard, and rejects the
    // whole call if one is not on it — so a missing dashboard entry does not degrade the passwordless link,
    // it takes out password reset entirely. That is what happened on Dev: this app's origin was never
    // registered, and Forgot Password went from working to erroring.
    //
    // Resetting a password is the important half and must not depend on a third-party dashboard entry that
    // only the "log in without password" link needs. So the login redirect is attempted, and dropped if
    // Stytch will not take it. The reset email still goes out either way.
    const startPasswordReset = (orgId: string, includeLoginRedirect: boolean) =>
        stytch.passwords.resetByEmailStart({
            organization_id: orgId,
            email_address: getValues('email'),
            reset_password_redirect_url: `${window.location.origin}/resetpassword`,
            // The reset email carries a second "log in without password" link, and Stytch only points it
            // somewhere useful if we say where. Without this it falls back to the project's default login
            // redirect URL, which is not this app — so the link goes nowhere (TTD-135).
            //
            // The bare origin rather than a per-organisation path, so this needs ONE allow-list entry rather
            // than a wildcard. TokenAuthenticator redeems the token from whatever route it lands on and then
            // sends the user to their org home using the slug on the authenticate response.
            ...(includeLoginRedirect ? { login_redirect_url: window.location.origin } : {}),
        });

    const handleForgotPasswordClick = async () => {
        clearErrors('root');
        try {
            const orgId = await getOrgIdFromSlug();

            // Remembered so that a spent or expired passwordless link can return the user to THIS sign-in
            // page. The link itself points at the bare origin, which is the organisation chooser — an
            // internal screen no ordinary user should ever be shown (TTD-135).
            //
            // A key of its own rather than AppContext's: that one is cleared on sign-out, which is exactly
            // when someone is most likely to be going through a password reset.
            localStorage.setItem('touchtab-portal-last-org-slug', orgSlug);

            try {
                await startPasswordReset(orgId, true);
            }
            catch (redirectErr) {
                // Retried without the login redirect rather than matched on an error type: Stytch's identifier
                // for this rejection is not something we can rely on, and any failure of the richer call is
                // worth answering with the simpler one. If that fails too, its error is what the user sees.
                console.warn(
                    'Password reset with a login redirect was rejected; retrying without it. ' +
                    `Register ${window.location.origin} as a redirect URL in the Stytch dashboard to restore ` +
                    'the "log in without password" link.',
                    redirectErr
                );
                await startPasswordReset(orgId, false);
            }

            toast({ severity: 'success', message: strings.forgotPasswordSent });
        }
        catch (err) {
            console.error('Error:', err);
            if (isStytchError(err)) {
                setError('root', { type: 'server', message: err.error_message });
            } else {
                setError('root', { type: 'server', message: 'An unexpected error occurred' });
            }
        }
    };

    return (        
        <Box            
            sx={{
                '& > :not(style)': { m: 1, width: '40ch' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100dvh', // full viewport (use 100vh if you prefer)
            }}
        >
            <Stack component="form" direction="column" onSubmit={validateAndSubmit(handleLogin)} noValidate>
                <img src={mode === 'dark' ? whiteLogoUrl : logoUrl} alt="TouchTabLogo" />
                <Spacer y={4} />
                {getOrganizationSlug() === "" &&
                    <>
                        <TextField
                            id="org-slug"
                            label="Organization"
                            variant="outlined"
                            value={orgSlug}
                            onChange={(e) => setOrgSlug(e.target.value)} />
                        <Spacer y={2} />
                    </>
                }
                <TextField
                    id="email"
                    type="email"
                    autoComplete="username"
                    label={strings.email}
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={!!formState.errors.email}
                    helperText={formState.errors.email?.message}
                    {...register('email', { required: strings.validationEmailRequired })} />
                <Spacer y={2} />
                <TextField
                    id="password"
                    label={strings.password}
                    type="password"
                    autoComplete="current-password"
                    variant="outlined"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                    error={!!formState.errors.password}
                    helperText={formState.errors.password?.message}
                    {...register('password', { required: strings.validationPasswordRequired })}
                />
                <Spacer y={1} />
                <Button type="submit" variant="contained" disabled={formState.isSubmitting}>{strings.signIn}</Button>
                <Spacer y={2} />
                <Button type="button" variant="text" onClick={handleForgotPasswordClick}>{strings.forgotPassword}</Button>
                <Spacer y={2} />
                {/* A refusal outranks a stale form error: it is the reason this screen is showing again. */}
                <Typography sx={{ alignSelf: 'center', whiteSpace: 'pre-line' }} color="error">
                    {refusalMessage ?? formState.errors.root?.message}
                </Typography>
            </Stack>
        </Box>      
    );
};

export default Login;
